import { useEffect, useMemo, useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  MenuItem, Stack, Typography, Alert, CircularProgress, Box,
} from "@mui/material";
import { useMutation } from "convex/react";
import { api } from "../../../convex/_generated/api";

const CATEGORIES = [
  { value: "nonexistent_transaction", label: "Nonexistent transaction" },
  { value: "duplicate", label: "Duplicate" },
  { value: "incorrect_value", label: "Incorrect value" },
  { value: "ocr_error", label: "OCR error" },
  { value: "invalid_vendor", label: "Invalid vendor" },
  { value: "other", label: "Other" },
];
const MIN = 10;
const isPosted = (r: any) => r.postingStatus === "posted" || r.qbImportStatus === "imported";

export function DeleteTransactionsModal({
  open, rows, deletedBy, onClose, onDone,
}: { open: boolean; rows: any[]; deletedBy: string; onClose: () => void; onDone: (msg: string) => void; }) {
  const softDeleteBulk = useMutation(api.transactions.softDeleteBulk);
  const [reason, setReason] = useState("");
  const [category, setCategory] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | undefined>();

  // Reset form state whenever the modal closes, so a stale reason/category/error
  // from a cancelled run is never reused for a different selection.
  useEffect(() => {
    if (!open) { setReason(""); setCategory(""); setError(undefined); }
  }, [open]);

  const postedCount = useMemo(() => rows.filter(isPosted).length, [rows]);
  const deletable = useMemo(() => rows.filter((r) => !isPosted(r)), [rows]);

  const submit = async () => {
    if (reason.trim().length < MIN || deletable.length === 0) return;
    setSubmitting(true); setError(undefined);
    // Track cross-chunk progress OUTSIDE the try so a mid-loop failure can still
    // report what already succeeded (the chunked loop is not atomic across chunks).
    let deleted = 0, blocked = 0;
    try {
      // chunk into batches of 200 (mutation cap)
      const ids = deletable.map((r) => r._id);
      for (let i = 0; i < ids.length; i += 200) {
        const r = await softDeleteBulk({
          ids: ids.slice(i, i + 200), reason: reason.trim(),
          category: (category || undefined) as any, deletedBy,
        });
        deleted += r.deleted.length; blocked += r.blocked.length;
      }
      onDone(`${deleted} deleted${blocked ? `, ${blocked} skipped` : ""}.`);
      setReason(""); setCategory(""); onClose();
    } catch (e: any) {
      const errMsg = e?.message ?? "Delete failed.";
      if (deleted > 0) {
        // Partial success: some chunks committed before the failure. Don't leave
        // the user with a silent partial deletion + an inline error — surface the
        // partial outcome via the snackbar, clear the selection, and close.
        onDone(`${deleted} deleted before an error occurred${blocked ? ` (${blocked} skipped)` : ""}. ${errMsg}`);
        setReason(""); setCategory(""); onClose();
      } else {
        // Nothing was deleted — keep the inline error and stay open for a retry.
        setError(errMsg);
      }
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Delete {deletable.length} transaction{deletable.length !== 1 ? "s" : ""}?</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {postedCount > 0 && (
            <Alert severity="warning">
              {postedCount} selected {postedCount === 1 ? "row is" : "rows are"} already posted to QuickBooks and will be skipped — use the reversal flow for those.
            </Alert>
          )}
          <Box sx={{ maxHeight: 180, overflowY: "auto" }}>
            {deletable.slice(0, 50).map((r) => (
              <Typography key={r._id} variant="caption" display="block" color="text.secondary">
                {r.date} · {r.vendorName ?? r.description} · {new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(r.amount)}
              </Typography>
            ))}
            {deletable.length > 50 && <Typography variant="caption">…and {deletable.length - 50} more</Typography>}
          </Box>
          <TextField label="Justification (required, min 10 chars)" value={reason}
            onChange={(e) => setReason(e.target.value)} multiline minRows={2} fullWidth
            error={reason.length > 0 && reason.trim().length < MIN}
            helperText={`${reason.trim().length}/${MIN}`} />
          <TextField select label="Category (optional)" value={category}
            onChange={(e) => setCategory(e.target.value)} fullWidth>
            <MenuItem value="">— none —</MenuItem>
            {CATEGORIES.map((c) => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
          </TextField>
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button color="error" variant="contained"
          disabled={reason.trim().length < MIN || deletable.length === 0 || submitting}
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
          onClick={submit}>
          {submitting ? "Deleting…" : `Delete ${deletable.length}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
export default DeleteTransactionsModal;
